import React, { useEffect, useMemo, useState } from "react";
import { FaTimes, FaPhone, FaSpinner } from "react-icons/fa";
import axiosInstance from "../config/axiosInstance";
import API from "../config/config";
import { toast } from "react-toastify";

const getPartyName = (ride, party) => {
  if (party === "Driver") {
    return ride?.driverId?.fullName || "N/A";
  }
  return ride?.userId?.fullName || "N/A";
};

const MobileNumberSharedModal = ({ show, ride, onClose, onSaved }) => {
  const [callerId, setCallerId] = useState("");
  const [sharedTimes, setSharedTimes] = useState("");
  const [party, setParty] = useState("Customer");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (show && ride) {
      setCallerId(ride.mobileNumberSharedCallerId || "");
      setSharedTimes(ride.mobileNumberSharedTimes || "");
      setParty(ride.mobileNumberSharedParty || "Customer");
    }
  }, [show, ride]);

  const partyName = useMemo(() => getPartyName(ride, party), [ride, party]);
  const noDriverAssigned = party === "Driver" && !ride?.driverId?.fullName;

  const handleClose = () => {
    setCallerId("");
    setSharedTimes("");
    setParty("Customer");
    onClose();
  };

  const handleSubmit = async () => {
    const trimmedCallerId = callerId.trim();
    const trimmedSharedTimes = sharedTimes.trim();

    if (!trimmedCallerId || !trimmedSharedTimes) {
      toast.error("Please enter both Caller ID and Shared Times");
      return;
    }

    if (!party || !["Customer", "Driver"].includes(party)) {
      toast.error("Please select Customer or Driver");
      return;
    }

    setLoading(true);
    try {
      const response = await axiosInstance.put(
        `${API.ADMIN.RIDE_MOBILE_NUMBER_SHARED}/${ride._id}/mobile-number-shared`,
        {
          mobileNumberShared: true,
          callerId: trimmedCallerId,
          sharedTimes: trimmedSharedTimes,
          party,
        }
      );

      if (response.data?.success) {
        toast.success("Mobile number shared updated");
        onSaved(response.data.data.ride);
        setCallerId("");
        setSharedTimes("");
        setParty("Customer");
      } else {
        toast.error(response.data?.message || "Failed to update mobile number shared");
      }
    } catch (error) {
      console.error("Mobile number shared update error:", error);
      toast.error(
        error.response?.data?.message || "Failed to update mobile number shared"
      );
    } finally {
      setLoading(false);
    }
  };

  if (!show || !ride) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <FaPhone className="text-blue-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">
              Mobile Number Shared
            </h3>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={loading}
          >
            <FaTimes />
          </button>
        </div>

        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
          <p className="text-sm font-medium text-gray-900 mb-1">
            Ride ID: {ride.rideId || ride._id}
          </p>
        </div>

        <div className="mb-4">
          <span className="block text-sm font-medium text-gray-700 mb-2">
            Shared with <span className="text-red-500">*</span>
          </span>
          <div className="flex gap-4">
            <label className="inline-flex items-center text-sm text-gray-700 cursor-pointer">
              <input
                type="radio"
                name="mobileNumberSharedParty"
                value="Customer"
                checked={party === "Customer"}
                onChange={() => setParty("Customer")}
                disabled={loading}
                className="mr-2"
              />
              Customer
            </label>
            <label className="inline-flex items-center text-sm text-gray-700 cursor-pointer">
              <input
                type="radio"
                name="mobileNumberSharedParty"
                value="Driver"
                checked={party === "Driver"}
                onChange={() => setParty("Driver")}
                disabled={loading}
                className="mr-2"
              />
              Driver
            </label>
          </div>
          <p className="mt-2 text-sm text-gray-600">
            {party === "Customer" ? "Customer" : "Driver"} name:{" "}
            <span className="font-medium text-gray-900">{partyName}</span>
          </p>
          {noDriverAssigned && (
            <p className="mt-1 text-xs text-amber-600">No driver assigned to this ride.</p>
          )}
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Caller ID <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={callerId}
            onChange={(e) => setCallerId(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            placeholder="Enter caller ID"
            disabled={loading}
          />
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Shared Times <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={sharedTimes}
            onChange={(e) => setSharedTimes(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            placeholder="Enter shared times"
            disabled={loading}
          />
        </div>

        <div className="flex justify-end space-x-3">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 inline-flex items-center"
            disabled={loading}
          >
            {loading ? (
              <>
                <FaSpinner className="animate-spin mr-2" />
                Saving...
              </>
            ) : (
              "Save"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MobileNumberSharedModal;
